import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, exhaustMap, map, of, switchMap, tap } from 'rxjs';
import * as FinanceManagementActions from './finance-management.actions';

import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { ListMyCompanyBillsResponse } from '../response/bills-list.response';
import {
  GET_COMPANY_BY_UUIDS,
  GET_MY_COMPANY_BILLS,
  SUBMIT_INTEGRATED_BANK_GUARANTEE,
  SUBMIT_MANUAL_BANK_GUARANTEE,
  UPDATE_MANUAL_BANK_GUARANTEE,
  SUBMIT_CASH_GUARANTEE,
  UPDATE_CASH_GUARANTEE,
  GET_BANKING_INTEGRATIONS,
  GET_BANK_GUARANTEE_SUBMISSIONS,
  GET_BANK_GUARANTEE_BY_UUID,
  GET_CASH_GUARANTEE_SUBMISSIONS,
  GET_CASH_GUARANTEE_BY_UUID,
  TOGGLE_BANK_GUARANTEE_STATUS,
  TOGGLE_CASH_GUARANTEE_STATUS,
  SUBMIT_FINANCIAL_SECURITY,
  GET_ALL_BANK_DETAILS,
  OMC_GUARANTEE_SUMMARY,
} from '../graphql/finance-management.graphql';
import { GET_BANK_GUARANTEE_TYPES } from '@modules/financial-security-configuration/graphql/financial-security-configuration.graphql';
import { GetBillResponse } from '../response/bill-by-uuid.response';
import {
  ManualBankGuaranteeRequest,
  SubmitCashGuaranteeDtoInput,
  SubmitIntegratedBankGuaranteeDtoInput,
  ToggleGuaranteeStatusDtoInput,
  UpdateCashGuaranteeDtoInput,
  UpdateManualBankGuaranteeDtoInput,
} from '../requests/bank-guarantee.request';
import {
  CashGuaranteeApiResponse,
  CashGuaranteeByUuidApiResponse,
  BankGuaranteeByUuidApiResponse,
  IntegratedBankGuaranteeApiResponse,
  ManualBankGuaranteeApiResponse,
  UpdateManualBankGuaranteeApiResponse,
  CashGuaranteeListApiResponse,
  BankGuaranteeListApiResponse,
  UpdateCashGuaranteeApiResponse,
  OmcGuaranteeSummaryResponse,
} from '../response/bank-guarantee.response';

@Injectable({
  providedIn: 'root',
})
export class FinanceManagementEffects {
  private actions$ = inject(Actions);
  private graphQLService = inject(GraphQlService);

  $getMyCompanyBills = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.CompanyBillsActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<ListMyCompanyBillsResponse>({
            query: GET_MY_COMPANY_BILLS,
            apolloNamespace: ApolloNamespace.billing,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listMyCompanyBills;
              if (!payload) {
                return FinanceManagementActions.CompanyBillsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch company bills',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.CompanyBillsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Company bills fetched successfully',
                  data: payload,
                },
              });
            }),
            catchError((err) =>
              of(
                FinanceManagementActions.CompanyBillsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getMyCompanyBillByUuid = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.CompanyBillByUuidActions.request),
      switchMap(({ request }) => {
        if (!request) {
          return of(
            FinanceManagementActions.CompanyBillByUuidActions.failure({
              error: {
                success: false,
                statusCode: 400,
                message: 'Bill UUID is required',
                data: null,
              },
            }),
          );
        }

        return (
          this.graphQLService
            .query<GetBillResponse>({
              query: GET_COMPANY_BY_UUIDS,
              apolloNamespace: ApolloNamespace.billing,
              variables: { uuid: request },
            })
            .pipe(
              map((apolloResult) => {
                const payload = apolloResult?.data?.getBillByUuid;
                if (!payload) {
                  return FinanceManagementActions.CompanyBillByUuidActions.failure({
                    error: {
                      success: false,
                      statusCode: 400,
                      message: 'Failed to fetch bill by UUID',
                      data: null,
                    },
                  });
                }
                return FinanceManagementActions.CompanyBillByUuidActions.success({
                  response: {
                    success: true,
                    statusCode: 200,
                    message: 'Bill fetched successfully',
                    data: payload,
                  },
                });
              }),
              catchError((err) =>
                of(
                  FinanceManagementActions.CompanyBillByUuidActions.failure({
                    error: {
                      success: false,
                      statusCode: 400,
                      message: err.message ?? 'Network error',
                      data: null,
                    },
                  }),
                ),
              ),
            )
        );
      }),
    ),
  );

  $getBankGuaranteeByUuid = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.BankGuaranteeByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<BankGuaranteeByUuidApiResponse>({
            query: GET_BANK_GUARANTEE_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankGuaranteeByUuid;
              if (!payload) {
                return FinanceManagementActions.BankGuaranteeByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch bank guarantee by UUID',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.BankGuaranteeByUuidActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Bank guarantee fetched successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((err) =>
              of(
                FinanceManagementActions.BankGuaranteeByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getCashGuaranteeByUuid = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.CashGuaranteeByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<CashGuaranteeByUuidApiResponse>({
            query: GET_CASH_GUARANTEE_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.cashGuaranteeByUuid;
              if (!payload) {
                return FinanceManagementActions.CashGuaranteeByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch cash guarantee by UUID',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.CashGuaranteeByUuidActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Cash guarantee fetched successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((err) =>
              of(
                FinanceManagementActions.CashGuaranteeByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: err.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitIntegratedBankGuarantee = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.IntegratedBankGuaranteeActions.request),
      switchMap(({ request }: { request: SubmitIntegratedBankGuaranteeDtoInput }) =>
        this.graphQLService
          .mutate<IntegratedBankGuaranteeApiResponse>({
            mutation: SUBMIT_INTEGRATED_BANK_GUARANTEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitIntegratedBankGuarantee;
              if (!payload) {
                return FinanceManagementActions.IntegratedBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to submit integrated bank guarantee',
                    data: null,
                  },
                });
              }
              const isSuccess = `${payload.status}`.toUpperCase() === 'SUCCESS';
              if (!isSuccess) {
                return FinanceManagementActions.IntegratedBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message ?? 'Failed to submit integrated bank guarantee',
                    data: payload.data ?? null,
                  },
                });
              }
              return FinanceManagementActions.IntegratedBankGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Integrated bank guarantee submitted successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.IntegratedBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitManualBankGuarantee = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ManualBankGuaranteeActions.request),
      switchMap(({ request }: { request: ManualBankGuaranteeRequest }) =>
        this.graphQLService
          .mutate<ManualBankGuaranteeApiResponse>({
            mutation: SUBMIT_MANUAL_BANK_GUARANTEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitManualBankGuarantee;
              if (!payload.status) {
                return FinanceManagementActions.ManualBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to submit manual bank guarantee',
                    data: null,
                  },
                });
              }

              return FinanceManagementActions.ManualBankGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Manual bank guarantee submitted successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ManualBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateManualBankGuarantee = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.UpdateManualBankGuaranteeActions.request),
      switchMap(({ request }: { request: { uuid: string; input: UpdateManualBankGuaranteeDtoInput } }) =>
        this.graphQLService
          .mutate<UpdateManualBankGuaranteeApiResponse>({
            mutation: UPDATE_MANUAL_BANK_GUARANTEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input, uuid: request.uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateManualBankGuarantee;
              if (!payload?.status) {
                return FinanceManagementActions.UpdateManualBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update manual bank guarantee',
                    data: payload?.data ?? null,
                  },
                });
              }

              return FinanceManagementActions.UpdateManualBankGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Manual bank guarantee updated successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.UpdateManualBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitCashGuarantee = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.CashGuaranteeActions.request),
      switchMap(({ request }: { request: SubmitCashGuaranteeDtoInput }) =>
        this.graphQLService
          .mutate<CashGuaranteeApiResponse>({
            mutation: SUBMIT_CASH_GUARANTEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitCashGuarantee;
              if (!payload.status) {
                return FinanceManagementActions.CashGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to submit cash guarantee',
                    data: null,
                  },
                });
              }

              console.log('Cash Guarantee Submission Payload:', payload);

              return FinanceManagementActions.CashGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Cash guarantee submitted successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.CashGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateCashGuarantee = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.UpdateCashGuaranteeActions.request),
      switchMap(({ request }: { request: { uuid: string; input: UpdateCashGuaranteeDtoInput } }) =>
        this.graphQLService
          .mutate<UpdateCashGuaranteeApiResponse>({
            mutation: UPDATE_CASH_GUARANTEE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request.input, uuid: request.uuid },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateCashGuarantee;
              if (!payload?.status) {
                return FinanceManagementActions.UpdateCashGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update cash guarantee',
                    data: payload?.data ?? null,
                  },
                });
              }

              return FinanceManagementActions.UpdateCashGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Cash guarantee updated successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.UpdateCashGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getBankConfigs = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.BankConfigsActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: GET_BANKING_INTEGRATIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankConfigs;
              if (!payload) {
                return FinanceManagementActions.BankConfigsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch bank configs',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.BankConfigsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Bank configs fetched successfully',
                  data: payload.content || [],
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.BankConfigsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $listIntegratedBankGuarantees = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ListIntegratedBankGuaranteeActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<BankGuaranteeListApiResponse>({
            query: GET_BANK_GUARANTEE_SUBMISSIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankGuaranteeSubmissions;
              if (!payload) {
                return FinanceManagementActions.ListIntegratedBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch integrated bank guarantees',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.ListIntegratedBankGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Integrated bank guarantees fetched successfully',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ListIntegratedBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $listManualBankGuarantees = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ListManualBankGuaranteeActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<BankGuaranteeListApiResponse>({
            query: GET_BANK_GUARANTEE_SUBMISSIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankGuaranteeSubmissions;
              if (!payload) {
                return FinanceManagementActions.ListManualBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch manual bank guarantees',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.ListManualBankGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Manual bank guarantees fetched successfully',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ListManualBankGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $listCashGuarantees = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ListCashGuaranteeActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<CashGuaranteeListApiResponse>({
            query: GET_CASH_GUARANTEE_SUBMISSIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.cashGuaranteeSubmissions;
              if (!payload) {
                return FinanceManagementActions.ListCashGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch cash guarantees',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.ListCashGuaranteeActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Cash guarantees fetched successfully',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ListCashGuaranteeActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $listAllBankGuarantees = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ListAllBankGuaranteeSubmissionsActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<BankGuaranteeListApiResponse>({
            query: GET_BANK_GUARANTEE_SUBMISSIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankGuaranteeSubmissions;
              if (!payload) {
                return FinanceManagementActions.ListAllBankGuaranteeSubmissionsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch bank guarantee submissions',
                    data: null,
                  },
                });
              }
              return FinanceManagementActions.ListAllBankGuaranteeSubmissionsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Bank guarantee submissions fetched successfully',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ListAllBankGuaranteeSubmissionsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getAllBankDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.BankDetailsActions.request),
      switchMap(() =>
        this.graphQLService
          .query<any>({
            query: GET_ALL_BANK_DETAILS,
            apolloNamespace: ApolloNamespace.billing,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.listAllBankDetails;
              return FinanceManagementActions.BankDetailsActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Bank details fetched successfully',
                  data: payload ?? [],
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.BankDetailsActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getBankGuaranteeTypes = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.BankGuaranteeTypesActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .query<any>({
            query: GET_BANK_GUARANTEE_TYPES,
            apolloNamespace: ApolloNamespace.registration,
            variables: { pageableParam: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.bankGuaranteeTypes;
              return FinanceManagementActions.BankGuaranteeTypesActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Bank guarantee types fetched successfully',
                  data: payload ?? { content: [] },
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.BankGuaranteeTypesActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $toggleManualBankGuaranteeStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ToggleManualBankGuaranteeStatusActions.request),
      switchMap(({ request }: { request: ToggleGuaranteeStatusDtoInput }) =>
        this.graphQLService
          .mutate<any>({
            mutation: TOGGLE_BANK_GUARANTEE_STATUS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request.uuid, active: request.active },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.toggleBankGuaranteeActive;
              const isSuccess = Boolean(payload?.status);

              if (!payload || !isSuccess) {
                return FinanceManagementActions.ToggleManualBankGuaranteeStatusActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update manual bank guarantee status',
                    data: payload?.data ?? null,
                  },
                });
              }

              return FinanceManagementActions.ToggleManualBankGuaranteeStatusActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Manual bank guarantee status updated successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ToggleManualBankGuaranteeStatusActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $toggleCashGuaranteeStatus = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.ToggleCashGuaranteeStatusActions.request),
      switchMap(({ request }: { request: ToggleGuaranteeStatusDtoInput }) =>
        this.graphQLService
          .mutate<any>({
            mutation: TOGGLE_CASH_GUARANTEE_STATUS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { uuid: request.uuid, active: request.active },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.toggleCashGuaranteeActive;
              const isSuccess = Boolean(payload?.status);

              if (!payload || !isSuccess) {
                return FinanceManagementActions.ToggleCashGuaranteeStatusActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update cash guarantee status',
                    data: payload?.data ?? null,
                  },
                });
              }

              return FinanceManagementActions.ToggleCashGuaranteeStatusActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Cash guarantee status updated successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.ToggleCashGuaranteeStatusActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitFinancialSecurity = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.SubmitFinancialSecurityActions.request),
      switchMap(({ request }) =>
        this.graphQLService
          .mutate<any>({
            mutation: SUBMIT_FINANCIAL_SECURITY,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.submitFinancialSecurity;
              const isSuccess = Boolean(payload?.status);

              if (!payload || !isSuccess) {
                return FinanceManagementActions.SubmitFinancialSecurityActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to submit financial security',
                    data: null,
                  },
                });
              }

              return FinanceManagementActions.SubmitFinancialSecurityActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Financial security submitted successfully',
                  data: payload.data ?? null,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.SubmitFinancialSecurityActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getOmcGuaranteeSummary = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceManagementActions.OmcGuaranteeSummaryActions.request),
      switchMap(() =>
        this.graphQLService
          .query<OmcGuaranteeSummaryResponse>({
            query: OMC_GUARANTEE_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.omcGuaranteeSummary;
              const isSuccess = Boolean(payload?.status);

              if (!payload || !isSuccess) {
                return FinanceManagementActions.OmcGuaranteeSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch OMC guarantee summary',
                    data: null,
                  },
                });
              }

              return FinanceManagementActions.OmcGuaranteeSummaryActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'OMC guarantee summary fetched successfully',
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                FinanceManagementActions.OmcGuaranteeSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Network error',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
